import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LifeBuoy,
  Plus,
  X,
  Send,
  Search,
  ChevronDown,
  ArrowLeft,
  CalendarPlus,
  Clock,
  Inbox,
  Paperclip,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { isAuthenticated } from '../services/otpAuthService';
import Pagination from '../components/Pagination';
import StatusBadge from '../components/support/StatusBadge';
import { requesterBadgeLabels } from '../components/support/displayStatus';
import PriorityBadge from '../components/support/PriorityBadge';
import {
  ACCEPTED_TYPES,
  FILE_ACCEPT,
  MAX_FILES,
  MAX_FILE_SIZE,
  REQUESTER_STATUS_LABELS,
  formatFileSize,
  formatShortDate,
} from '../components/support/constants';
import useDialogFocus from '../components/support/requester/useDialogFocus';
import {
  countRequesterTickets,
  filterRequesterTickets,
  needsRequesterAction,
  paginate,
  sortRequesterTickets,
  type RequesterListTab,
} from '../components/support/requester/requesterModel';
import {
  getGroups,
  getMyTickets,
  createTicket,
  SupportGroupLite,
  SupportTicketListItem,
} from '../services/supportService';
import { channelEmojiPrefix } from '../components/support/channelAvatar';
import { CHIAVE_MONOSPACE, campiRichiestaDi, valoriDaInviare } from '../components/support/campiPratica';

const priorityOptions = [
  { value: 'bassa', label: 'Bassa' },
  { value: 'media', label: 'Media' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

// Canale che eredita il vecchio modulo Feedback: qui la richiesta porta due
// classificazioni aggiuntive (tipo e categoria) salvate come tag del ticket.
const FEEDBACK_SLUG = 'feedback';

const tipoOptions = [
  { value: 'feedback', label: 'Feedback' },
  { value: 'bug', label: 'Segnalazione problema' },
  { value: 'suggestion', label: 'Suggerimento' },
];

const categoriaOptions = [
  { value: 'area-riservata', label: 'Area riservata' },
  { value: 'rete-certificata', label: 'Rete Certificata' },
  { value: 'svincolo-atti', label: 'Svincolo atti' },
  { value: 'rimborso-voucher', label: 'Rimborso voucher' },
  { value: 'blog', label: 'Blog' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'generale', label: 'Generale' },
  { value: 'altro', label: 'Altro' },
];

const ITEMS_PER_PAGE = 10;

/**
 * Contatori, pillole e ricerca ragionano sull'INSIEME delle richieste, non
 * sulla pagina a schermo: l'elenco arriva in blocchi grossi e poi si impagina
 * qui (10 per pagina, come prima). Il tetto evita che un elenco anomalo faccia
 * decine di chiamate.
 */
const FETCH_CHUNK = 100;
const MAX_TICKETS = 500;

const CARD = 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800';
const FIELD_LABEL = 'block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5';
const INPUT =
  'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const TABS: { value: RequesterListTab; label: string }[] = [
  { value: 'in_corso', label: 'In corso' },
  { value: 'concluse', label: 'Concluse' },
  { value: 'tutte', label: 'Tutte' },
];

const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // List state
  const [tickets, setTickets] = useState<SupportTicketListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<RequesterListTab>('in_corso');
  const [search, setSearch] = useState('');

  // Groups
  const [groups, setGroups] = useState<SupportGroupLite[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // New ticket modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [subject, setSubject] = useState('');
  const [priority, setPriority] = useState('media');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [tipo, setTipo] = useState('feedback');
  const [categoria, setCategoria] = useState('generale');
  // «Dati della pratica» del canale scelto (es. Documento Unico): si azzerano
  // a ogni cambio di canale, cosi un valore di un canale non finisce in un altro.
  const [campiValori, setCampiValori] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // I parametri URL (?nuovo=1&canale=...) vanno applicati una volta sola.
  const paramsApplied = useRef(false);
  const modalPanelRef = useRef<HTMLDivElement>(null);
  const canaleSelectRef = useRef<HTMLSelectElement>(null);

  // Modale: fuoco sul primo campo all'apertura, Tab confinato, e alla chiusura
  // il fuoco torna al bottone che l'ha aperta.
  useDialogFocus(showNewModal, modalPanelRef, { initialRef: canaleSelectRef });

  // Auth check
  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Load tickets. `isCancelled` viene dalla pulizia dell'effetto: se la pagina
  // si smonta a meta caricamento il ciclo si ferma li, senza chiedere i blocchi
  // successivi e senza scrivere stato su un albero ormai morto.
  const loadTickets = useCallback(async (isCancelled: () => boolean = () => false) => {
    setLoading(true);
    try {
      const all: SupportTicketListItem[] = [];
      let next = 1;
      // Si continua solo finche il server dichiara che ce ne sono altri E la
      // pagina ha davvero prodotto righe: senza la seconda condizione un
      // `total` sbagliato manderebbe il ciclo al tetto.
      for (;;) {
        const res = await getMyTickets({ page: next, limit: FETCH_CHUNK });
        if (isCancelled()) return;
        const items = res.items || [];
        all.push(...items);
        const total = res.total || all.length;
        if (items.length === 0 || all.length >= total || all.length >= MAX_TICKETS) break;
        next += 1;
      }
      setTickets(all);
    } catch (error) {
      if (isCancelled()) return;
      toast.error('Errore nel caricamento delle richieste');
    } finally {
      if (!isCancelled()) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load groups
  const loadGroups = useCallback(async () => {
    try {
      const data = await getGroups();
      // Canali nascosti agli associati (Task 31): mai selezionabili, nemmeno via ?canale=.
      setGroups((data || []).filter((g) => !g.hiddenFromAssociates));
    } catch {
      // Silent: la lista canali non è critica per la pagina
    } finally {
      setGroupsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) return;
    let cancelled = false;
    loadTickets(() => cancelled);
    return () => { cancelled = true; };
  }, [loadTickets]);

  useEffect(() => {
    if (isAuthenticated()) {
      loadGroups();
    }
  }, [loadGroups]);

  // ESC to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showNewModal) setShowNewModal(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showNewModal]);

  const resetForm = () => {
    setGroupId('');
    setSubject('');
    setPriority('media');
    setBody('');
    setFiles([]);
    setTipo('feedback');
    setCategoria('generale');
    setCampiValori({});
  };

  const selectedGroup = groups.find((g) => g._id === groupId);
  const isFeedbackGroup = selectedGroup?.slug === FEEDBACK_SLUG;
  const campiRichiesta = useMemo(() => campiRichiestaDi(selectedGroup?.campiRichiesta), [selectedGroup]);

  const cambiaCanale = (id: string) => {
    setGroupId(id);
    setCampiValori({});
  };

  // Apertura diretta da link esterno (es. la card "Contattaci & Segnalazioni"):
  // ?nuovo=1 apre la modale, ?canale=<slug> preseleziona il canale,
  // ?tipo= e ?categoria= preimpostano le due select del canale feedback,
  // ?oggetto= precompila l'oggetto (es. dalla guida formalità e canali).
  useEffect(() => {
    if (paramsApplied.current || !groupsLoaded) return;
    if (searchParams.get('nuovo') !== '1') return;
    paramsApplied.current = true;

    resetForm();
    const slug = searchParams.get('canale');
    const target = slug ? groups.find((g) => g.slug === slug) : undefined;
    if (target) setGroupId(target._id);
    const t = searchParams.get('tipo');
    if (t && tipoOptions.some((o) => o.value === t)) setTipo(t);
    const c = searchParams.get('categoria');
    if (c && categoriaOptions.some((o) => o.value === c)) setCategoria(c);
    const oggetto = searchParams.get('oggetto');
    if (oggetto) setSubject(oggetto.slice(0, 200));
    setShowNewModal(true);

    // Ripulisce la query: chiudendo la modale il refresh non la riapre.
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, groupsLoaded, searchParams]);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = ''; // consente di riselezionare lo stesso file
    if (selected.length === 0) return;

    const valid: File[] = [];
    for (const f of selected) {
      if (!ACCEPTED_TYPES.includes(f.type)) {
        toast.error(`Tipo file non ammesso: ${f.name}`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`"${f.name}" supera i 10MB`);
        continue;
      }
      valid.push(f);
    }

    setFiles((prev) => {
      const merged = [...prev, ...valid];
      if (merged.length > MAX_FILES) {
        toast.error(`Massimo ${MAX_FILES} file`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || (!body.trim() && files.length === 0)) {
      toast.error('Compila oggetto e descrizione (o allega un file)');
      return;
    }
    setIsSubmitting(true);
    try {
      const created = await createTicket({
        // Senza canale la richiesta va nel canale predefinito di smistamento.
        groupId: groupId || undefined,
        subject: subject.trim(),
        priority,
        body: body.trim(),
        files: files.length > 0 ? files : undefined,
        tags: isFeedbackGroup ? [`tipo:${tipo}`, `categoria:${categoria}`] : undefined,
        campiExtra: valoriDaInviare(campiRichiesta, campiValori),
      });
      toast.success('Richiesta inviata con successo!');
      setShowNewModal(false);
      resetForm();
      navigate(`/area-riservata/supporto/${created._id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Errore durante l\'invio');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Derivati di lista (funzioni pure, testate a parte) ---
  const counters = useMemo(() => countRequesterTickets(tickets), [tickets]);
  const visible = useMemo(
    () => sortRequesterTickets(filterRequesterTickets(tickets, tab, search)),
    [tickets, tab, search]
  );
  const totalPages = Math.max(1, Math.ceil(visible.length / ITEMS_PER_PAGE));
  const pageItems = useMemo(
    () => paginate(visible, Math.min(page, totalPages), ITEMS_PER_PAGE),
    [visible, page, totalPages]
  );

  // Cambiare filtro o ricerca riporta alla prima pagina: restare alla pagina 4
  // di un elenco che ora ne ha 1 mostrerebbe il vuoto.
  useEffect(() => {
    setPage(1);
  }, [tab, search]);

  const openNewModal = () => {
    resetForm();
    setShowNewModal(true);
  };

  const counterCards: { key: string; label: string; value: number; tone: string }[] = [
    { key: 'inCorso', label: 'In corso', value: counters.inCorso, tone: 'text-indigo-600 dark:text-indigo-400' },
    { key: 'daLeggere', label: 'Da leggere', value: counters.daLeggere, tone: 'text-amber-600 dark:text-amber-400' },
    { key: 'concluse', label: 'Concluse', value: counters.concluse, tone: 'text-gray-900 dark:text-white' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Intestazione */}
        <button
          onClick={() => navigate('/area-riservata')}
          className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Area Riservata
        </button>

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-indigo-100 dark:bg-indigo-900/40">
              <LifeBuoy className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </span>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Supporto</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Le tue richieste ai canali tematici UNASCA
              </p>
            </div>
          </div>
          <button
            onClick={openNewModal}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuova richiesta</span>
            <span className="sm:hidden">Nuova</span>
          </button>
        </div>

        {/* Contatori */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          {counterCards.map((c) => (
            <div key={c.key} role="group" aria-label={c.label} className={`${CARD} p-3`}>
              <p className={`text-lg font-semibold tabular-nums ${c.tone}`}>{c.value}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-0.5">
                {c.label}
              </p>
            </div>
          ))}
        </div>

        {/* Filtri */}
        <div className="flex flex-wrap items-center gap-2 mt-5">
          <div className="flex items-center gap-1.5" role="group" aria-label="Filtra per stato">
            {TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                aria-pressed={tab === t.value}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  tab === t.value
                    ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                    : 'border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per numero o oggetto..."
              aria-label="Cerca per numero o oggetto"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Lista */}
        <div className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : pageItems.length === 0 ? (
            <div className={`${CARD} text-center py-16`}>
              <Inbox className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                Nessuna richiesta trovata
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {tickets.length === 0
                  ? 'Apri una nuova richiesta per ricevere assistenza dal team UNASCA'
                  : 'Prova a cambiare filtro o testo di ricerca'}
              </p>
              <button
                onClick={openNewModal}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nuova richiesta
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {pageItems.map((t) => {
                const unread = !!t.unread?.associate;
                const action = needsRequesterAction(t);
                return (
                  <button
                    key={t._id}
                    type="button"
                    onClick={() => navigate(`/area-riservata/supporto/${t._id}`)}
                    className={`w-full text-left ${CARD} p-4 transition-all hover:shadow-md ${
                      action ? 'border-amber-300 dark:border-amber-700' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {unread && (
                        <span
                          className="w-2.5 h-2.5 mt-1.5 rounded-full bg-green-500 shrink-0"
                          title="Nuova risposta"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-gray-400 dark:text-gray-500 shrink-0">
                            #{t.ticketNumber}
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {t.subject}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {t.groupId?.nome && (
                            <span className="inline-flex items-center gap-1.5">
                              <LifeBuoy className="w-3.5 h-3.5" aria-hidden="true" />
                              {t.groupId.nome}
                            </span>
                          )}
                          {t.createdAt && (
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarPlus className="w-3.5 h-3.5" aria-hidden="true" />
                              Aperta il {formatShortDate(t.createdAt)}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                            {formatShortDate(t.lastActivityAt)}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <span className="flex items-center gap-1.5">
                          <PriorityBadge priority={t.priority} />
                          <span title={requesterBadgeLabels(t, REQUESTER_STATUS_LABELS).title}>
                            <StatusBadge status={t.status} labels={requesterBadgeLabels(t, REQUESTER_STATUS_LABELS).labels} dot />
                          </span>
                        </span>
                        {action && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                            Tocca a te
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={Math.min(page, totalPages)}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    totalItems={visible.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal: Nuova richiesta */}
      {showNewModal && (
        <div
          className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowNewModal(false); }}
        >
          <div
            ref={modalPanelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="nuova-richiesta-titolo"
            className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700"
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 id="nuova-richiesta-titolo" className="text-sm font-semibold text-gray-900 dark:text-white">
                Nuova richiesta
              </h2>
              <button
                onClick={() => setShowNewModal(false)}
                aria-label="Chiudi"
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              {/* Canale */}
              {groups.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  La tua richiesta verrà assegnata al gruppo competente.
                </p>
              ) : (
              <div>
                <label htmlFor="nuova-canale" className={FIELD_LABEL}>Canale</label>
                <div className="relative">
                  <select
                    id="nuova-canale"
                    ref={canaleSelectRef}
                    value={groupId}
                    onChange={(e) => cambiaCanale(e.target.value)}
                    className={`${INPUT} appearance-none`}
                  >
                    <option value="">Non so / Supporto generale</option>
                    {groups.map((g) => (
                      <option key={g._id} value={g._id}>{channelEmojiPrefix(g.avatar)}{g.nome}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                {selectedGroup?.descrizione && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                    {selectedGroup.descrizione}
                  </p>
                )}
              </div>
              )}

              {/* Dati della pratica: solo per i canali che li prevedono (Documento Unico) */}
              {campiRichiesta.length > 0 && (
                <fieldset className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 space-y-3">
                  <legend className="px-1 text-xs font-semibold text-gray-700 dark:text-gray-200">
                    Dati della pratica (facoltativi)
                  </legend>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Se li hai, aiutano il canale a trovare subito la pratica.
                  </p>
                  {campiRichiesta.map((c) => {
                    const id = `nuova-campo-${c.chiave}`;
                    return (
                      <div key={c.chiave}>
                        <label htmlFor={id} className={FIELD_LABEL}>{c.etichetta}</label>
                        <input
                          id={id}
                          type="text"
                          value={campiValori[c.chiave] || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCampiValori((prev) => ({ ...prev, [c.chiave]: v }));
                          }}
                          maxLength={c.maxLength}
                          autoComplete="off"
                          autoCapitalize={c.chiave === CHIAVE_MONOSPACE ? 'characters' : undefined}
                          className={`${INPUT}${c.chiave === CHIAVE_MONOSPACE ? ' font-mono uppercase' : ''}`}
                        />
                      </div>
                    );
                  })}
                </fieldset>
              )}

              {/* Tipo e categoria: solo per il canale feedback/segnalazioni */}
              {isFeedbackGroup && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="nuova-tipo" className={FIELD_LABEL}>Tipo</label>
                    <div className="relative">
                      <select
                        id="nuova-tipo"
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value)}
                        className={`${INPUT} appearance-none`}
                      >
                        {tipoOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="nuova-categoria" className={FIELD_LABEL}>Categoria</label>
                    <div className="relative">
                      <select
                        id="nuova-categoria"
                        value={categoria}
                        onChange={(e) => setCategoria(e.target.value)}
                        className={`${INPUT} appearance-none`}
                      >
                        {categoriaOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
              )}

              {/* Oggetto */}
              <div>
                <label htmlFor="nuova-oggetto" className={FIELD_LABEL}>Oggetto *</label>
                <input
                  id="nuova-oggetto"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Oggetto della richiesta..."
                  className={INPUT}
                  maxLength={200}
                />
              </div>

              {/* Priorita */}
              <div>
                <label htmlFor="nuova-priorita" className={FIELD_LABEL}>Priorità</label>
                <div className="relative">
                  <select
                    id="nuova-priorita"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className={`${INPUT} appearance-none`}
                  >
                    {priorityOptions.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Descrizione */}
              <div>
                <label htmlFor="nuova-descrizione" className={FIELD_LABEL}>Descrizione *</label>
                <textarea
                  id="nuova-descrizione"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Descrivi la tua richiesta nel dettaglio..."
                  rows={5}
                  className={`${INPUT} resize-none`}
                  maxLength={5000}
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-right tabular-nums">
                  {body.length}/5000
                </p>
              </div>

              {/* Allegati */}
              <div>
                <span className={FIELD_LABEL}>Allegati</span>
                <label className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors">
                  <Paperclip className="w-4 h-4 shrink-0" />
                  <span>Aggiungi file</span>
                  <input
                    type="file"
                    accept={FILE_ACCEPT}
                    multiple
                    onChange={handleFilesSelected}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Max {MAX_FILES} file, 10MB ciascuno · PDF, JPG, PNG, WEBP, Word, Excel
                </p>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {files.map((f, i) => (
                      <li
                        key={`${f.name}-${i}`}
                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/60 text-xs"
                      >
                        <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-gray-700 dark:text-gray-200">
                          {f.name}
                        </span>
                        <span className="text-gray-400 dark:text-gray-500 shrink-0">
                          {formatFileSize(f.size)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors shrink-0"
                          aria-label={`Rimuovi ${f.name}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || !subject.trim() || (!body.trim() && files.length === 0)}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Invia richiesta
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
